import { useAuth, useOrganization, useUser } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useMemo } from "react";
import { getMyStatusApiUsersMeGetOptions } from "../generated/api/@tanstack/react-query.gen";
import { identifyUser, resetAnalytics, startRecording } from "../lib/analytics";
import { setTokenGetter } from "../lib/api-client";
import {
  type BillingMetadata,
  deriveBillingStatus,
  type OrgMetadata,
  type SessionClaimsWithPlan,
} from "../lib/billing-status";
import { gallerySessionsByScopeQuery } from "../lib/queries";
import {
  type BillingStatus,
  BillingStatusContext,
} from "./BillingStatusContext";

/**
 * Stand-in limit used only until GET /api/users/me answers, or if it fails.
 * Paired with 0 used, it means "assume allowance remains" — the server is the
 * real gate, and guessing "out of iterations" would wrongly overlay the app.
 */
const FALLBACK_ITERATION_LIMIT = 50;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { getToken, isSignedIn, sessionClaims, orgId } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();

  useEffect(() => {
    setTokenGetter(() => getToken());
  }, [getToken]);

  const setSentryUser = useEffectEvent(() => {
    Sentry.setUser(
      user
        ? { id: user.id, email: user.emailAddresses[0]?.emailAddress }
        : null,
    );
  });

  useEffect(() => {
    setSentryUser();
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    queryClient.prefetchInfiniteQuery(
      gallerySessionsByScopeQuery({ projectId: null }, orgId),
    );
  }, [queryClient, user?.id, orgId]);

  const { data: usageStatus, isPending: isUsageStatusPending } = useQuery({
    ...getMyStatusApiUsersMeGetOptions(),
    enabled: Boolean(isSignedIn && user),
  });

  const billingStatus = useMemo<BillingStatus | null>(() => {
    if (!(isSignedIn && user)) return null;

    return deriveBillingStatus(
      user.publicMetadata as BillingMetadata,
      (sessionClaims as SessionClaimsWithPlan | null | undefined) ?? null,
      (organization?.publicMetadata as OrgMetadata | undefined) ?? null,
      {
        // Fail open if the status request errored: the API is the real gate,
        // and a user who is genuinely out of iterations just gets a 403 from
        // the generation endpoint. Reporting 0 used against a non-zero limit
        // is the shape that means "assume there is allowance left".
        used: usageStatus?.iterations_used ?? 0,
        limit: usageStatus?.iterations_limit ?? FALLBACK_ITERATION_LIMIT,
      },
    );
  }, [isSignedIn, user, sessionClaims, organization, usageStatus]);

  const billingStatusValue = useMemo(
    () => ({
      billingStatus,
      // Wait for the usage numbers before letting the UI decide — otherwise a
      // user with allowance left would briefly see the out-of-iterations
      // overlay. Subscribers don't need them, so they never block on the
      // round-trip.
      isBillingLoading:
        billingStatus === null ||
        (!billingStatus.has_active_subscription && isUsageStatusPending),
    }),
    [billingStatus, isUsageStatusPending],
  );

  // Identify the user in PostHog and start a session recording for paid ("power")
  // users only. Free users are identified and tracked but not recorded. Internal
  // accounts (admins and billing-exempt/comped users) are also excluded from
  // recording so replays reflect real customers — matching the analytics cohort
  // that filters the same users out of insights. Kept separate from Sentry
  // (Sentry = errors, PostHog = behaviour).
  useEffect(() => {
    if (user && billingStatus) {
      identifyUser(user.id, {
        email: user.emailAddresses[0]?.emailAddress,
        plan: billingStatus.active_plan,
        is_admin: billingStatus.is_admin,
        billing_exempt: billingStatus.billing_exempt,
        org_id: orgId ?? null,
      });
      const isPaidExternalUser =
        billingStatus.has_active_subscription &&
        !billingStatus.is_admin &&
        !billingStatus.billing_exempt;
      if (isPaidExternalUser) {
        startRecording();
      }
    } else if (isSignedIn === false) {
      resetAnalytics();
    }
  }, [user, billingStatus, isSignedIn, orgId]);

  return (
    <BillingStatusContext.Provider value={billingStatusValue}>
      {children}
    </BillingStatusContext.Provider>
  );
}
